import React, { createContext, useContext, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import PropTypes from 'prop-types';

import { useMediaQuery } from '@frontend/shared-utils';
import { Accordion } from '@frontend/shared-ui-kit/accordion';

import { getClass } from '../../../ship/utils/component-helpers';
import Column from './column';
import { CheckboxCell } from './cells';
import Icon from '../icon';

import { formatCellValue } from '../../base/common/utils';
import Tooltip from '../../misc/tooltip';
import classNames from 'classnames';
import Checkbox from '../checkbox';
import { SkeletonLoader } from '../skeleton-loader';

const TableContext = createContext<any>({});
const useTableContext = () => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error(
      `Table compound components cannot be rendered outside the Table component`,
    );
  }
  return context;
};

export type TableProps = {
  data: any[];
  columns: any[];
  children: React.ReactNode;
  rowIdField: string;
  totals?: any;
  subRowData?: any[];
  stickyColumnsCount?: number;
  rowPath?: () => void;
  selectLimit?: number;
  selectedRows: { [k: string]: { value: any } };
  selectable: boolean;
  selectedColors: { [k: string]: { id: string; color: string } };
  onSort: () => void;
  sortOrder: string;
  hasOneProductCategory: boolean;
  stateSetters: { [setter: string]: (state: any) => void };
};

const Table = (props: any) => {
  const {
    children,
    data,
    subRowData,
    onAllRowsSelect,
    onRowSelect,
    onRowClick,
    onSort,
    rowIdField,
    rowPath,
    selectable,
    selectedColors,
    selectLimit,
    selectedRows,
    sortField,
    sortOrder,
    stickyColumnsCount,
    totals,
    visibleColumns,
    sortIconClassName,
    scrollHeight,
    rowClasses,
    raisedRowIndex,
    totalLabelColumnIndex,
    rowSize,
    bgColor,
    headerSize,
    tableHeight,
    headerWrap,
    disableTableScroll,
    rowWidth,
    openAccordions,
    showAllRowsDeselect,
    headerOverride,
    headerBgColor,
    subRowKey,
    totalsRowBg,
    subRowBg,
    newSortIcons,
    tableClassName,
    increasedHeightRows,
    headerHeight,
    openRows,
    optionalState,
    hasOneProductCategory,
    pageParams,
    stateSetters,
    allSites,
  } = props;

  const router = useRouter();
  const allFlags = useFlags();
  const [shadow, setShadow] = useState(false);

  let currentRowWidth = rowWidth ? rowWidth : 'auto';
  if (useMediaQuery('(min-width: 1950px)')) {
    currentRowWidth = 'auto';
  }
  const isMobile = useMediaQuery('(max-width: 640px)');
  const scrollDiv = useRef(null);
  const columns = React.Children.toArray(children);
  const selectedCount =
    selectable &&
    selectedRows &&
    Object.values(selectedRows).filter((val) => val).length;

  const onTableScroll = (e: any) => {
    const { scrollLeft } = e.target;
    setShadow(scrollLeft > 0);
  };

  const setSort = (field: any) => {
    let order;
    if (sortField !== field) {
      order = 'desc';
    } else {
      order = sortOrder === 'desc' ? 'asc' : 'desc';
    }
    onSort({ field, order });
  };

  const columnJustification = (justifyContent: any) => {
    return {
      'justify-start': justifyContent === 'left',
      'justify-center': justifyContent === 'center',
      'justify-end': justifyContent === 'right',
    };
  };

  const sortIcon = (sortDirection: any) => {
    if (sortDirection === 'desc') {
      return newSortIcons ? 'sortDescending' : 'caretDown';
    } else {
      return newSortIcons ? 'sortAscending' : 'caretUp';
    }
  };

  const HeaderRow = () => {
    let stickyWidth = selectable ? 48 : 0;

    const headerColumns = columns.map((col, columnIndex) => {
      if (!React.isValidElement(col)) return;
      const {
        sortable,
        headerIcon,
        label,
        colHeaderOverride,
        subLabel,
        subLabelRender,
        dataKey,
        justifyContent,
        width,
        flexWidth,
        isShowing,
        labelRender,
        fixed,
        className,
        noMargin,
        flags,
        sideBorder,
        tooltip,
        noLineClamp,
      } = col.props;

      const hasFeatureFlag = flags
        ? flags.every((f: any) => allFlags[f])
        : true;

      if (
        ((visibleColumns && !visibleColumns.has(dataKey)) ||
          !isShowing ||
          !hasFeatureFlag) &&
        !fixed
      ) {
        return null;
      }
      const labelContent = labelRender
        ? labelRender({ label, optionalState })
        : label;

      const getSortIcon = () => {
        if (!sortable || sortField !== dataKey) return null;
        const sortDirection = sortIcon(sortOrder);
        return (
          <span
            data-cy="sort-icon"
            className={classNames('ml-1', sortIconClassName)}
          >
            <Icon size={14} iconName={sortDirection} />
          </span>
        );
      };

      const isSticky = columnIndex < stickyColumnsCount;
      const addShadow =
        shadow && isSticky && columnIndex === stickyColumnsCount - 1;
      const stickyStyles = isSticky
        ? {
            left: stickyWidth,
          }
        : {};
      stickyWidth += width;

      const widthStyles = !flexWidth
        ? {
            minWidth: width && `${width}px`,
            flexGrow: `0`,
            flexShrink: `0`,
            flexBasis: `0`,
            ...stickyStyles,
          }
        : {
            flexGrow: `${width}`,
            flexBasis: `${width}px`,
            flexShrink: 0,
            ...stickyStyles,
          };

      return (
        <th
          className={getClass(
            className,
            'flex flex-row items-center h-full border-b bg-inherit border-neutral-gray-300 box-border',
            columnJustification(justifyContent),
            sideBorder instanceof Function
              ? sideBorder(optionalState)
              : sideBorder,
            dataKey,
            {
              'after:absolute after:-right-2 after:w-2 after:h-full after:shadow-sticky':
                addShadow,
              'sm:sticky sm:z-10': isSticky,
            },
          )}
          data-cy={`column-header-${dataKey}`}
          key={`${columnIndex}-header-${dataKey}`}
          onClick={() => sortable && setSort(dataKey)}
          style={{
            cursor: sortable ? 'pointer' : 'default',
            ...widthStyles,
          }}
        >
          {colHeaderOverride ? (
            colHeaderOverride
          ) : (
            <div
              className={getClass(
                'text-sm flex items-center text-right',
                {
                  'mx-4': !noMargin,
                  'whitespace-nowrap': headerWrap,
                },
                headerSize || 'text-base font-medium',
              )}
            >
              <div className="flex flex-col">
                <div
                  className={classNames('flex flex-row leading-tight', {
                    'text-left justify-start': justifyContent === 'left',
                    'text-right justify-end': justifyContent === 'right',
                    'text-center justify-center': justifyContent === 'center',
                    'line-clamp-2': !noLineClamp,
                  })}
                >
                  {labelContent}
                  {headerIcon && <Icon iconName={headerIcon} />}
                  {tooltip && <div>{tooltip}</div>}
                </div>
                {subLabelRender
                  ? subLabelRender({ subLabel, optionalState })
                  : subLabel}
              </div>
              {getSortIcon()}
            </div>
          )}
        </th>
      );
    });

    return (
      <tr
        className={
          rowClasses ||
          `flex relative items-center min-w-fit ${
            headerBgColor || bgColor
          } ${headerHeight}`
        }
        style={{ width: currentRowWidth }}
      >
        {selectable ? (
          onAllRowsSelect ? (
            <CheckboxCell
              onSelect={onAllRowsSelect}
              checked={selectedCount === data.length}
              color={'default'}
              showDeselect={showAllRowsDeselect}
            />
          ) : (
            <th
              className={`sticky ${bgColor} flex flex-none items-center left-0 h-full border-b border-neutral-gray-300 box-border w-12 px-4`}
            />
          )
        ) : null}
        {headerOverride || headerColumns}
      </tr>
    );
  };

  const StatRow = ({
    key,
    rowData,
    rowIndex,
    selected,
    raisedRowIndex,
    lastRow,
    isSubRow,
    increasedRowHeight,
    loading,
    subRowsData,
    rowIsOpened,
  }: any) => {
    let stickyWidth = selectable ? 48 : 0;

    return (
      <tr
        key={key}
        id={`stat-row-${key}`}
        data-cy={`stat-row-${key}`}
        className={
          rowClasses ||
          classNames(
            'flex group relative items-center min-w-fit hover:bg-hover-violet transition-height duration-200 ease-in-out',
            {
              'h-32': rowSize === 'md' && !increasedRowHeight,
              'h-16': rowSize === 'sm' && !increasedRowHeight,
              [subRowBg]: isSubRow && subRowBg,
              'bg-inherit': !isSubRow || !subRowBg,
            },
          )
        }
        style={{
          zIndex: raisedRowIndex === rowIndex ? 2 : 'initial',
          width: currentRowWidth,
          height: increasedRowHeight,
        }}
        onClick={(e) => onRowClick(rowIndex, e, isSubRow, rowData[rowIdField])}
      >
        {selectable && (
          <CheckboxCell
            checked={!!selected}
            color={'default'}
            stopPropagation={true}
            onSelect={() => {
              if (selectLimit && !selected && selectedCount >= selectLimit)
                return;
              onRowSelect({
                rowData,
                rowId: rowData[rowIdField],
              });
            }}
            lastRow={lastRow}
            hidden={isSubRow || !rowData[rowIdField]}
          />
        )}
        {columns.map((col, columnIndex) => {
          if (!React.isValidElement(col)) return;
          const {
            dataKey,
            justifyContent,
            width,
            flexWidth,
            tooltip,
            fontColor,
            isShowing,
            fixed,
            className,
            labelClassName,
            keepMobileLabel,
            cellClassName,
            noMargin,
            label,
            flags,
            sideBorder,
          } = col.props;

          const hasFeatureFlag = flags
            ? flags.every((f: any) => allFlags[f])
            : true;

          if (
            ((visibleColumns && !visibleColumns.has(dataKey)) ||
              !isShowing ||
              !hasFeatureFlag) &&
            !fixed
          ) {
            return null;
          }

          const isSticky = columnIndex < stickyColumnsCount;
          const addShadow =
            shadow && isSticky && columnIndex === stickyColumnsCount - 1;
          const stickyStyles = isSticky
            ? { left: stickyWidth, backgroundColor: 'inherit' }
            : {};
          stickyWidth += width;
          const widthStyles = !flexWidth
            ? {
                minWidth: width && `${width}px`,
                flexGrow: `0`,
                flexShrink: `0`,
                flexBasis: `0`,
                ...stickyStyles,
              }
            : {
                flexGrow: `${width}`,
                flexBasis: `${width}px`,
                flexShrink: 0,
                ...stickyStyles,
              };

          if (loading) {
            return (
              <td key={`${key}-${columnIndex}-loading-row`} style={widthStyles}>
                <SkeletonLoader
                  className={'w-full h-16'}
                  textCount={1}
                  orientation={'horizontal'}
                />
              </td>
            );
          }

          const content = getCellRenderValue({
            columnInfo: col,
            rowData,
            rowIndex,
            columnIndex,
            isSubRow,
            subRowsData,
            rowIsOpened,
          });

          const columnContent =
            rowPath && !columnIndex ? (
              <div
                onClick={() => {
                  router.push(rowPath(rowData));
                }}
              >
                {content}
              </div>
            ) : (
              content
            );

          return (
            <td
              key={`${rowIndex}-${columnIndex}-${dataKey}`}
              className={getClass(
                className,
                'flex flex-row items-center h-full border-neutral-gray-300 box-border min-w-0 group-hover:bg-hover-violet',
                lastRow ? 'border-0' : 'border-b',
                sideBorder instanceof Function
                  ? sideBorder(optionalState)
                  : sideBorder,
                dataKey,
                columnJustification(justifyContent),
                {
                  'after:absolute after:-right-2 after:w-2 after:h-full after:shadow-sticky':
                    addShadow,
                  'sm:sticky': isSticky,
                  [subRowBg]: isSubRow && subRowBg,
                  'bg-inherit': !isSubRow || !subRowBg,
                },
              )}
              data-cy={`column-${dataKey}`}
              style={widthStyles}
            >
              {(columnIndex > 0 || keepMobileLabel) && (
                <div
                  className={
                    labelClassName ||
                    'sm:sr-only w-1/2 text-right pt-1 font-semibold text-gray-600 break-normal'
                  }
                >
                  {label}
                </div>
              )}
              <div
                className={
                  cellClassName ||
                  getClass(
                    `text-sm py-1`,
                    `${fontColor ? fontColor(content) : ''}`,
                    {
                      'mx-4': !noMargin,
                    },
                  )
                }
                ref={(node) => {
                  return (
                    tooltip &&
                    new Tooltip(node, {
                      text: content,
                      size: width,
                      fixedSize: true,
                      noTail: false,
                    })
                  );
                }}
              >
                {columnContent}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  const TotalsRow = ({ data }: any) => {
    let stickyWidth = selectable ? 48 : 0;
    return (
      <tr
        className={classNames(
          'flex relative items-center min-w-fit',
          totalsRowBg || 'bg-deep-space-10',
          {
            'h-32': rowSize === 'md',
            'h-16': rowSize === 'sm',
          },
        )}
        style={{ width: currentRowWidth }}
      >
        {selectable && (
          <td
            className={`sticky flex flex-none items-center left-0 h-full border-b border-neutral-gray-300 box-border w-12 bg-inherit`}
          />
        )}
        {columns.map((col, columnIndex) => {
          if (!React.isValidElement(col)) return;
          const {
            dataKey,
            justifyContent,
            width,
            totalRender,
            format,
            flexWidth,
            isShowing,
            fixed,
            noMargin,
            label,
            labelClassName,
            cellClassName,
            flags,
            sideBorder,
          } = col.props;

          const hasFeatureFlag = flags
            ? flags.every((f: any) => allFlags[f])
            : true;

          if (
            ((visibleColumns && !visibleColumns.has(dataKey)) ||
              !isShowing ||
              !hasFeatureFlag) &&
            !fixed
          ) {
            return null;
          }

          let cellData = data[dataKey];

          if (typeof cellData !== 'undefined' && cellData !== null && format) {
            cellData = formatCellValue(cellData, format.type, format.options);
          }

          let content = '';
          const totalLabelColumn =
            totalLabelColumnIndex === columnIndex ||
            (columnIndex === 0 && !totalLabelColumnIndex);
          if (totalRender) {
            content = totalRender({
              cellData,
              columnIndex,
              columnInfo: col,
              rowData: data,
            });
          } else if (
            totalLabelColumnIndex &&
            columnIndex < totalLabelColumnIndex
          ) {
            content = '';
          } else if (totalLabelColumn) {
            content = 'Total';
          } else if (typeof cellData === 'undefined' || cellData === null) {
            content = '-';
          } else {
            content = String(cellData);
          }

          const isSticky = columnIndex < stickyColumnsCount;
          const addShadow =
            shadow && isSticky && columnIndex === stickyColumnsCount - 1;
          const className = getClass(
            'flex flex-row items-center h-full border-b border-neutral-gray-300 bg-inherit box-border min-w-0',
            totalLabelColumn
              ? 'justify-start'
              : columnJustification(justifyContent),
            sideBorder instanceof Function
              ? sideBorder(optionalState)
              : sideBorder,
            dataKey,
            {
              'after:absolute after:-right-2 after:w-2 after:h-full after:shadow-sticky':
                addShadow,
              'sm:sticky': isSticky,
            },
          );
          const stickyStyles = isSticky
            ? {
                left: stickyWidth,
                backgroundColor: 'inherit',
              }
            : {};
          stickyWidth += width;
          const widthStyles = !flexWidth
            ? {
                minWidth: width && `${width}px`,
                flexGrow: `0`,
                flexShrink: `0`,
                flexBasis: `0`,
                ...stickyStyles,
              }
            : {
                flexGrow: `${width}`,
                flexBasis: `${width}px`,
                flexShrink: 0,
                ...stickyStyles,
              };

          return (
            <td
              key={`total-${dataKey}`}
              className={className}
              style={widthStyles}
            >
              {columnIndex > 0 && (
                <div
                  className={
                    labelClassName ||
                    'sm:sr-only w-1/2 text-right pt-1 font-semibold text-gray-600'
                  }
                >
                  {label}
                </div>
              )}
              <div
                className={
                  cellClassName ||
                  getClass(
                    'text-sm',
                    columnIndex === 0 || totalLabelColumnIndex === columnIndex
                      ? 'text-neutral-gray-500'
                      : null,
                    {
                      'mx-4': !noMargin,
                    },
                  )
                }
              >
                {content}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  const getCellRenderValue = (
    {
      columnInfo,
      rowData,
      rowIndex,
      columnIndex,
      isSubRow,
      subRowsData,
      rowIsOpened,
    }: {
      columnInfo: any;
      rowData: any;
      rowIndex: number;
      columnIndex: number;
      isSubRow: boolean;
      subRowsData: any;
      rowIsOpened?: boolean;
    },
    isTotal?: boolean,
  ) => {
    const { dataKey, format, cellRender, totalRender } = columnInfo.props;
    let cellData = rowData[dataKey];

    if (![null, undefined].includes(cellData) && format) {
      cellData = formatCellValue(cellData, format.type, format.options);
    }

    const renderFn = isTotal ? totalRender : cellRender;

    const content = renderFn
      ? renderFn({
          cellData,
          rowData,
          rowIndex,
          columnIndex,
          columnInfo: columnInfo.props,
          isMobile,
          isSubRow,
          subRowsData,
          rowIsOpened,
          hasOneProductCategory,
          pageParams,
          stateSetters,
          allSites,
        })
      : typeof cellData === 'undefined' || cellData === null
      ? '-'
      : String(cellData);

    return content;
  };

  //  Mobile Render Components
  const RowItem = (
    {
      rowData,
      rowIndex,
      selected,
      key,
    }: { rowData: any; rowIndex?: number; selected?: boolean; key?: string },
    isTotal?: boolean,
  ) => {
    return (
      <div className={'mb-4 md:mx-4 px-4 sm:px-0'} key={key}>
        <Accordion className={'relative'} open={openAccordions}>
          <Accordion.Toggle className={isTotal ? 'bg-deep-space-10' : bgColor}>
            <div className={'flex items-center text-sm w-full'}>
              {selectable && !isTotal && (
                <Checkbox
                  checked={!!selected}
                  color={'default'}
                  //  TODO Refactor, Dup code with StatRow
                  stopPropagation={true}
                  onSelect={() => {
                    if (
                      selectLimit &&
                      !selected &&
                      selectedCount >= selectLimit
                    )
                      return;
                    onRowSelect({
                      rowData,
                      rowId: rowData[rowIdField],
                    });
                  }}
                />
              )}
              <div className={'flex flex-col w-full mr-2'}>
                {isTotal ? (
                  <div className={'font-medium'}>Total</div>
                ) : (
                  <>
                    <div>
                      {getCellRenderValue({
                        columnInfo: columns[0],
                        rowData,
                        rowIndex,
                        columnIndex: 0,
                      } as any)}
                    </div>
                    {/* <div>
                      {React.isValidElement(columns[0])
                        ? columns[0].props.label
                        : null}
                    </div> */}
                  </>
                )}
              </div>
            </div>
          </Accordion.Toggle>
          <Accordion.Panel>
            <div className={'m-4 text-sm'}>
              {columns.slice(1).map((col, columnIndex) => {
                if (
                  !React.isValidElement(col) ||
                  (visibleColumns && !visibleColumns.has(col.props.dataKey))
                )
                  return null;
                return (
                  <div
                    key={`${col.props.dataKey}-${rowData[col.props.dataKey]}`}
                    className={'flex justify-between md:mx-4 my-2'}
                  >
                    <div className={'font-medium'}>
                      {col.props.label} {col.props.subLabel}
                    </div>
                    <div>
                      {getCellRenderValue(
                        {
                          columnInfo: col,
                          rowData,
                          rowIndex,
                          columnIndex: columnIndex + 1,
                        } as any,
                        isTotal,
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Accordion.Panel>
        </Accordion>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div>
        {totals && RowItem({ rowData: totals }, true)}
        {data.map((rowData: any, rowIndex: any) => {
          return RowItem({
            key: rowData[rowIdField],
            rowData,
            rowIndex,
            selected: selectedRows && !!selectedRows[rowData[rowIdField]],
          });
        })}
      </div>
    );
  }

  return (
    <div className={classNames('relative', tableHeight)}>
      <table
        className={classNames(
          `flex relative flex-col w-full h-full table-fixed lining-nums tabular-nums ${bgColor}`,
          { ['overflow-auto']: !disableTableScroll },
          tableClassName,
        )}
        onScroll={onTableScroll}
      >
        <thead className="sticky top-0 z-10">{HeaderRow()}</thead>
        <tbody
          className="relative height-calc bg-inherit"
          ref={scrollDiv}
          style={
            scrollHeight ? { maxHeight: scrollHeight, overflowY: 'scroll' } : {}
          }
        >
          {totals && TotalsRow({ data: totals })}
          {data.map((rowData: any, index: any) => {
            const statRow = StatRow({
              key: rowData[rowIdField],
              rowData,
              rowIndex: index,
              selected: selectedRows && !!selectedRows[rowData[rowIdField]],
              raisedRowIndex,
              lastRow: index === data.length - 1 && !rowData.rowIsOpened,
              isSubRow: false,
              increasedRowHeight: increasedHeightRows[rowData[rowIdField]],
              subRowsData: subRowData,
              rowIsOpened: openRows[rowData?.[rowIdField]],
            });

            const subRows = (() => {
              if (!openRows[rowData?.[rowIdField]]) return null;

              if (rowData?.rows?.length) {
                return rowData.rows.map((subRow: any, subIdx: any) => {
                  return StatRow({
                    key: subRow[subRowKey],
                    rowData: subRow,
                    rowIndex: subIdx,
                    selected: false,
                    raisedRowIndex: false,
                    lastRow: false,
                    isSubRow: true,
                    increasedRowHeight: null, // TODO: Not sure if we want to add variable row height to sub rows
                  });
                });
              }

              if (subRowData?.[rowData?.[rowIdField]]) {
                return subRowData[rowData[rowIdField]].map(
                  (subRow: any, subIdx: any) => {
                    return StatRow({
                      key: `subRow[subRowKey]-${subIdx}`,
                      rowData: subRow,
                      rowIndex: subIdx,
                      selected: false,
                      raisedRowIndex: false,
                      lastRow: false,
                      isSubRow: true,
                      increasedRowHeight: null, // TODO: Not sure if we want to add variable row height to sub rows
                    });
                  },
                );
              } else {
                return StatRow({
                  key: `loading-${rowData[rowIdField]}`,
                  rowData: {},
                  rowIndex: 0,
                  selected: false,
                  raisedRowIndex: false,
                  lastRow: true,
                  isSubRow: true,
                  increasedRowHeight: null,
                  loading: true,
                });
              }

              return null;
            })();

            return (
              <React.Fragment key={rowData[rowIdField]}>
                {statRow}
                {subRows}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any).isRequired, // TODO: set this to use any to circumvent TS error
  subRowData: PropTypes.any,
  totals: PropTypes.object,
  stickyColumnsCount: PropTypes.number,
  rowIdField: PropTypes.string.isRequired,
  rowPath: PropTypes.func,
  selectLimit: PropTypes.number,
  selectable: PropTypes.bool,
  selectedColors: PropTypes.shape({
    id: PropTypes.string,
    color: PropTypes.string,
  }),
  selectedRows: PropTypes.object,
  onSort: PropTypes.func,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  sortField: PropTypes.string,
  children: PropTypes.arrayOf(Column).isRequired,
  onAllRowsSelect: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  visibleColumns: PropTypes.instanceOf(Set),
  sortIconClassName: PropTypes.string,
  //  This prop is used for special scenarios where a dropdown or any absolute positioned element
  //  needs to attach to a sticky column element.
  raisedRowIndex: PropTypes.number,
  totalLabelColumnIndex: PropTypes.number,
  rowClasses: PropTypes.string,
  rowSize: PropTypes.oneOf(['sm', 'md']),
  bgColor: PropTypes.string,
  headerSize: PropTypes.string,
  tableHeight: PropTypes.string,
  headerWrap: PropTypes.bool,
  disableTableScroll: PropTypes.bool,
  rowWidth: PropTypes.string,
  scrollHeight: PropTypes.number,
  openAccordions: PropTypes.bool,
  showAllRowsDeselect: PropTypes.bool,
  headerOverride: PropTypes.any,
  headerBgColor: PropTypes.string,
  subRowKey: PropTypes.string,
  totalsRowBg: PropTypes.string,
  subRowBg: PropTypes.string,
  newSortIcons: PropTypes.bool,
  tableClassName: PropTypes.string,
  increasedHeightRows: PropTypes.object,
  headerHeight: PropTypes.string,
  openRows: PropTypes.any,
  optionalState: PropTypes.any,
  hasOneProductCategory: PropTypes.bool,
  pageParams: PropTypes.any,
  stateSetters: PropTypes.any,
  allSites: PropTypes.bool,
};

Table.defaultProps = {
  subRowData: {},
  onAllRowsSelect: null,
  onRowSelect: null,
  onRowClick: () => {},
  totals: null,
  sortField: null,
  sortOrder: null,
  onSort: null,
  rowPath: null,
  selectLimit: null,
  selectedRows: {},
  selectedColors: {},
  selectable: false,
  stickyColumnsCount: 0,
  visibleColumns: null,
  sortIconClassName: '',
  raisedRowIndex: null,
  rowSize: 'md',
  bgColor: 'bg-white',
  totalLabelColumnIndex: 0,
  headerSize: '',
  tableHeight: null,
  headerWrap: false,
  disableTableScroll: false,
  rowWidth: null,
  openAccordions: false,
  showAllRowsDeselect: false,
  headerOverride: null,
  headerBgColor: null,
  subRowKey: null,
  totalsRowBg: null,
  subRowBg: null,
  newSortIcons: false,
  tableClassName: null,
  increasedHeightRows: {},
  headerHeight: 'h-12',
  openRows: {},
  optionalState: {},
  hasOneProductCategory: null,
  stateSetters: {},
  allSites: false,
};

export default Table;
